/* External */
import {
  useCallback,
  useEffect,
  useState,
  useMemo,
  memo,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import { withFocusable } from '@noriginmedia/react-spatial-navigation';
import { useIntl, FormattedMessage } from 'react-intl';

/* Ott-common */
import InteractiveElement from '@dtvgo/interactive-element';
import Icon from '@dtvgo/icon';
import { useIsDelayed } from '@dtvgo/player-utils-react';
import { usePlayerDuration } from '@dtvgo/player-context';
import Toast from '@dtvgo/toast';

/* Components */
import { useDeviceInfo } from '~/providers/DeviceInfoProvider';
import ContentInformation from './ContentInformation';
import RatingInformation from './RatingInformation';
import ProgressBarContainer from './ProgressBarContainer';
import TracksControls from './TracksControls';
import LoaderScreen from './LoaderScreen';
import LoaderRipple from './LoaderRipple';
import MiniEpg from '../MiniEpg';
import GoLive from './GoLive';
import AreYouStillWatching from '../AreYouStillWatching';
import FocusableDiv from '../../FocusableDiv';
import ConditionalVisible from './ConditionalVisible';
import FocusableIconButton from '../../FocusableIconButton';
import TapppPanel from '../TapppPanel';
import UpsellPlayerBanner from '../../UpsellPlayerBanner';

/* Hooks */
import { useToggleSpatialNavigation } from '../../../providers/RestoreFocusProvider';
import useClickCallback from '../../../hooks/useClickCallback';
import { useOnBackPress, useOnKeyDown, useOnLongPress } from '../../../utils/keyDownEvents';
import useSelectedEpgFilter from '../../../hooks/useSelectedEpgFilter';
import useEpgFilters from '../../../hooks/useEpgFilters';
import useShowAndHide from '../../../hooks/useShowAndHide';
import useAreYouStillWatching from './utils/useAreYouStillWatching';
import useUserInteraction from './utils/useUserInteraction';
import useControlsVisibility from './utils/useControlsVisibility';
import useRefSync from '../../../hooks/useRefSync';
import { useUserAccount } from '../../../providers/UserProvider';
/* Services */
import { gaService } from '../../../services/gaService';

/* Providers */
import { useTermsOfServiceInfo } from '../../../providers/TermsOfServiceProvider';

/* Utils */
import { noOp, returnFalse } from '../../../utils/functions';
import { COMPONENTS } from '../../../utils/focusKey';
import { useTitle } from '../../../utils/metaDataUtils';
import { getPlayerSecondSubtitle, usePlayerSubtitle } from './utils';
import {
  EPG_FILTERS_ID,
  REPORT_GA,
  PROFILE_VIEWED_INTERACTIVITY_BUTTON,
} from '../../../utils/constants';
import platform from '../../../utils/platform';
import { ClassNames } from '../../../utils/misc';
import { Direction } from '../../../utils/spatialNavigation';
import { GTM_EVENT_NAMES, GTM_SUBCATEGORIES } from '../../../utils/googleTags';
import parentalControlBlock from '../../../utils/parentalControlUtils';
import { shouldUserAcceptNewTerms } from '../../../providers/TermsOfServiceProvider/utils';
import {
  nextSpeed, ScrubbingDirection, ScrubbingSpeed, SCRUBBING_MAX_SPEED,
} from './FocusableProgressBar';
import {
  isTapppSDKActiveForOsVersion,
  isTapppPanelEnabledForUser,
} from '../../../utils/profiles';

/* Styles */
import './PlayerUI.scss';

const ACTIVATION_DURATION = 500;
const HIDE_RATING_AFTER_MS = 5000;
const SHOW_PLAY_PAUSE_DELAY_TIME = 1000;

const CHANNEL_BUTTONS = Object.values(platform.keyCodes.channel);
const CHANNEL_BUTTONS_SET = new Set(CHANNEL_BUTTONS);

export const PlayerUIClassNames = ClassNames.component('player-ui');

/* eslint sonarjs/cognitive-complexity: "warn" -- Mark as warning to fix in the future */
function PlayerUI({
  className = '',
  pauseOnShowControls = false,

  // Metadata
  schedule = undefined,
  content = undefined,
  isContentRequestCompleted = false,
  channelId = '',
  isLive = false,
  isBetAllowed = false,
  showButtonAnimation = false,
  hasNextEpisode = false,
  isFromInterstitial = false,
  homeOnBack = false,
  counterEpisode = 0,
  channelName = '',

  // Player State
  isReadyToPlay = false,
  isPlaying = false,
  isSeeked = false,
  isBuffering = true,
  isSourceUnloaded = false,
  audioTracks = [],
  subtitleTracks = [],

  // Event listeners
  onUserInteraction = noOp,
  onToggleMiniEpg = noOp,

  // Business actions
  goToNextEpisode = noOp,

  // Player actions
  updateSubtitles = noOp,
  updateAudio = noOp,
  play = noOp,
  pause = noOp,
  setProgress,
  selectAudioTrack,
  selectSubtitleTrack,
  disableSubtitles,
  goToLive,

  // Upper-state mutators
  setPlayerReducedSize = noOp,

  // Google Tags
  playerCommonTagEvent = noOp,

  // Upsell
  playerUpsellMessage = '',
  playerUpsellLink = '',

  hasPlaybackAuthorizerSuccess = undefined,
  setFocus,
}) {
  const [isScrubInProgress, setIsScrubInProgress] = useState(false);
  const [warningToastText, setWarningToastText] = useState('profile.tapppPanel.error.toast.title');
  const [channelSwitchedInMiniEpg, setChannelSwitchedInMiniEpg] = useState(false);
  const wasFocusedActionButtons = useRef(false);
  const [iconName, setIconName] = useState('');
  const isPlayingRef = useRefSync(isPlaying);
  const contentRatingRef = useRefSync(content?.rating);
  const contentId = content?.vrioAssetId;
  const previousContentId = useRef(contentId);
  const iconTimeout = useRef(undefined);
  const seekingToStart = useRef(false);
  const isNextEpisodeLoader = useRef(false);
  const sendRewindEvent = useRef(false);
  const isDelayed = useIsDelayed(isLive);
  const [isContentUpdated, setIsContentUpdated] = useState(false);
  const videoDuration = usePlayerDuration();
  const { uid: userId, iso2Code } = useUserAccount() || {};
  const [termsAgreementVerified, setTermsAgreementVerified] = useState(false);
  const { setCurrentPath, performShouldUserAcceptNewTerms } = useTermsOfServiceInfo();
  const hasBetsInteractivity = schedule?.interactivities?.bets || content?.interactivities?.bets;
  const { sdkVersion } = useDeviceInfo();

  /* Tappp Panel Interactivity */
  const [showToast, setShowToast] = useState(false);

  const shouldShowBetsInteractivityButton = hasBetsInteractivity
    && isTapppPanelEnabledForUser(iso2Code, userId);

  const shouldDisableInteractivityButton = !isBetAllowed;
  const shouldShowWarningToast = isBetAllowed
    && !isTapppSDKActiveForOsVersion(platform.osName, sdkVersion);

  const showWarningToast = useCallback(() => {
    setShowToast(true);
    setTimeout(() => setShowToast(false), 8000);
  }, []);

  const { timeZone } = useIntl();

  const { pauseSpatialNavigation, resumeSpatialNavigation } = useToggleSpatialNavigation();

  const { lastUserInteraction, handleUserInteraction } = useUserInteraction(onUserInteraction);

  const {
    mounted: mountedTracks,
    displaying: displayingTracks,
    refs: tracksRefs,
    show: showTracks,
    hide: hideTracks,
  } = useShowAndHide(false);

  const {
    mounted: mountedRating,
    displaying: displayingRating,
    show: showRating,
    hide: hideRating,
  } = useShowAndHide(false);

  const {
    mounted: mountedMiniEpg,
    displaying: displayingMiniEpg,
    refs: miniEpgRefs,
    show: showMiniEpg,
    hide: hideMiniEpg,
  } = useShowAndHide(false);

  const {
    mounted: mountedTapppPanel,
    displaying: displayingTapppPanel,
    refs: tapppPanelRefs,
    show: showTapppPanel,
    hide: hideTapppPanel,
  } = useShowAndHide(false);

  let showRipple = (isReadyToPlay || isPlaying) && (isBuffering || isSourceUnloaded);

  if (pauseOnShowControls) {
    showRipple = showRipple && !channelSwitchedInMiniEpg;
  }

  const [isStillWatchingActive, onContinueWatching] = useAreYouStillWatching({
    isReadyToPlay,
    isLive,
    isDelayed,
    isPlaying,
    counterEpisode,
    lastUserInteraction,
    handleUserInteraction,
    play,
    pause,
    goToLive,
  });
  const isStillWatchingActiveRef = useRefSync(isStillWatchingActive);

  const displayingNotMainControls = displayingMiniEpg
    || displayingTracks
    || displayingRating
    || displayingTapppPanel
    || isStillWatchingActive;
  const displayingNotMainControlsRef = useRefSync(displayingNotMainControls);

  const {
    mountedControls,
    displayingControls,
    showControls,
    showControlsAndReport,
    hideControls,
    handleMouseEnter,
    handleMouseLeave,
  } = useControlsVisibility({
    handleUserInteraction,
    isPlaying,
    pauseOnShowControls,
    pause,
    displayingNotMainControls,
  });

  const handleContinueWatching = useCallback(() => {
    onContinueWatching();
    showControls();
    setFocus(COMPONENTS.PROGRESS_BAR);
  }, [onContinueWatching, showControls, setFocus]);

  const { filtersMiniEpg } = useEpgFilters();
  const {
    channels,
    selectedFilter,
    handleFilterSelect,
    isFetching,
  } = useSelectedEpgFilter({
    filters: filtersMiniEpg,
    defaultFilter: EPG_FILTERS_ID.FOR_YOU,
    isMiniEpgFilters: true,
    readyToFetch: isLive && isPlaying,
  });

  const sendTagEvent = useCallback((isRewind = false) => {
    sendRewindEvent.current = true;

    const getTagEventData = () => {
      if (content) {
        const eventName = isRewind
          ? GTM_EVENT_NAMES.PLAYER.FORWARD
          : GTM_EVENT_NAMES.PLAYER.BACKWARD;

        if ((content?.type === 'EPISODE' || hasNextEpisode) && !isLive) {
          playerCommonTagEvent({
            subcategory: GTM_SUBCATEGORIES.PLAYER.SERIE,
            eventName,
          });
        } else if (isLive) {
          playerCommonTagEvent({
            subcategory: GTM_SUBCATEGORIES.PLAYER.LIVE,
            eventName,
          });
        } else {
          playerCommonTagEvent({
            subcategory: GTM_SUBCATEGORIES.PLAYER.MOVIE,
            eventName,
          });
        }
      }
    };

    if (sendRewindEvent) {
      getTagEventData();
      sendRewindEvent.current = false;
    }
  }, [playerCommonTagEvent, isLive, content, hasNextEpisode]);

  const handleEnterPressSchedules = useCallback((event) => {
    if (isReadyToPlay) {
      playerCommonTagEvent({ eventName: GTM_EVENT_NAMES.PLAYER.MORE_CHANNELS });
      showMiniEpg();
      hideTapppPanel();
      hideTracks();
      setChannelSwitchedInMiniEpg(true);
      hideControls();
      setPlayerReducedSize(true);
      handleUserInteraction();
      const { keyCode } = event;

      if (pauseOnShowControls && CHANNEL_BUTTONS_SET.has(keyCode)) {
        pause();
      }

      switch (keyCode) {
        case platform.keyCodes.channel.up: {
          gaService.sendEvent({
            action: REPORT_GA.GA_EVENT_ACTION_OPEN_MINI_EPG,
            label: REPORT_GA.GA_EVENT_LABEL_OPEN_MINI_EPG_CH_UP,
            category: REPORT_GA.GA_EVENT_CATEGORY_OPEN_MINI_EPG,
          });
          break;
        }
        case platform.keyCodes.channel.down: {
          gaService.sendEvent({
            action: REPORT_GA.GA_EVENT_ACTION_OPEN_MINI_EPG,
            label: REPORT_GA.GA_EVENT_LABEL_OPEN_MINI_EPG_CH_DOWN,
            category: REPORT_GA.GA_EVENT_CATEGORY_OPEN_MINI_EPG,
          });
          break;
        }
        default: {
          gaService.sendEvent({
            action: REPORT_GA.GA_EVENT_ACTION_OPEN_MINI_EPG,
            label: REPORT_GA.GA_EVENT_LABEL_OPEN_MINI_EPG_ICON_BUTTON,
            category: REPORT_GA.GA_EVENT_CATEGORY_OPEN_MINI_EPG,
          });
        }
      }
    }
  }, [
    pauseOnShowControls,
    pause,
    showMiniEpg,
    hideTapppPanel,
    hideControls,
    hideTracks,
    setPlayerReducedSize,
    handleUserInteraction,
    isReadyToPlay,
    playerCommonTagEvent,
  ]);

  const lastFocused = useRef(COMPONENTS.PROGRESS_BAR);

  const handleHideTapppPanel = useCallback(() => {
    hideTapppPanel();
    if (displayingControls) showControls();
    setPlayerReducedSize(false);
    const elementToFocus = lastFocused.current === COMPONENTS.TAPPP_BUTTON
      ? COMPONENTS.TAPPP_BUTTON : COMPONENTS.PROGRESS_BAR;

    if (displayingControls) setFocus(elementToFocus);
  }, [hideTapppPanel, setFocus, setPlayerReducedSize, showControls, displayingControls]);

  const handleHideMiniEpg = useCallback(() => {
    hideMiniEpg();
    showControls();
    setPlayerReducedSize(false);
    setFocus(COMPONENTS.MINI_EPG_BUTTON);
  }, [hideMiniEpg, setFocus, setPlayerReducedSize, showControls]);

  const handleEnterPressSubtitles = useCallback(() => {
    updateAudio();
    updateSubtitles();
    hideMiniEpg();
    hideTapppPanel();
    showTracks();
    hideControls();
    setPlayerReducedSize(true);
    handleUserInteraction();
    playerCommonTagEvent({ eventName: GTM_EVENT_NAMES.PLAYER.LANGUAGE });
  }, [
    updateAudio,
    updateSubtitles,
    hideMiniEpg,
    hideTapppPanel,
    showTracks,
    hideControls,
    setPlayerReducedSize,
    handleUserInteraction,
    playerCommonTagEvent,
  ]);

  const handleEnterPressTapppPanel = useCallback(async () => {
    if (shouldShowWarningToast) {
      setWarningToastText('profile.tapppPanel.error.toast.title');
      showWarningToast();
      return;
    }

    try {
      const shouldShowTerms = await shouldUserAcceptNewTerms(iso2Code?.toLowerCase());
      if (shouldShowTerms && isTapppSDKActiveForOsVersion(platform.osName, sdkVersion)) {
        setWarningToastText('player.tapppPanel.terms.toast.title');
        showWarningToast();
        return;
      }
    } catch (error) {
      console.error('PlayerUI>handleEnterPressTapppPanel', error);
      setWarningToastText('player.tapppPanel.terms.toast.error');
      showWarningToast();
      return;
    }

    if (shouldDisableInteractivityButton) return;

    showTapppPanel();
    hideTracks();
    hideControls();
    hideMiniEpg();
    setPlayerReducedSize(true);
    handleUserInteraction();
    playerCommonTagEvent({
      subcategory: GTM_SUBCATEGORIES.PLAYER.LIVE,
      eventName: GTM_EVENT_NAMES.PLAYER.INTERACTIVITY,
    });
  }, [
    shouldDisableInteractivityButton,
    shouldShowWarningToast,
    showWarningToast,
    showTapppPanel,
    hideTracks,
    hideControls,
    hideMiniEpg,
    setPlayerReducedSize,
    handleUserInteraction,
    playerCommonTagEvent,
    iso2Code,
    sdkVersion,
  ]);

  const handleHideTracks = useCallback(() => {
    hideTracks();
    showControls();
    setPlayerReducedSize(false);
    setFocus(COMPONENTS.TRACK_BUTTON);
    playerCommonTagEvent({
      subcategory: GTM_SUBCATEGORIES.PLAYER.AUDIO_AND_SUBTITLES,
      eventName: GTM_EVENT_NAMES.PLAYER.CLOSE,
    });
  }, [hideTracks, playerCommonTagEvent, setFocus, setPlayerReducedSize, showControls]);

  const isScrubInProgressRef = useRefSync(isScrubInProgress);
  const handleProgressBarArrowPress = useCallback((direction) => {
    if (isScrubInProgressRef.current) return false;
    if (direction === Direction.UP && !wasFocusedActionButtons.current) {
      if (isLive) {
        if (isDelayed) {
          setFocus(COMPONENTS.GO_LIVE_BUTTON);
        } else {
          setFocus(COMPONENTS.MINI_EPG_BUTTON);
        }
      } else {
        setFocus(COMPONENTS.BACK_TO_START);
      }
      return false;
    }
    return true;
  }, [isDelayed, isLive, isScrubInProgressRef, setFocus]);

  const handleFocusedActionButtons = useCallback(() => {
    wasFocusedActionButtons.current = true;
  }, []);

  const saveLastFocusedComponent = useCallback((_, { realFocusKey }) => {
    lastFocused.current = realFocusKey;
  }, []);

  const handleEnterPressGoLive = useCallback(() => {
    setFocus(COMPONENTS.PROGRESS_BAR);
    goToLive();
    handleUserInteraction();
    playerCommonTagEvent({ eventName: GTM_EVENT_NAMES.PLAYER.LIVE });
  }, [setFocus, goToLive, handleUserInteraction, playerCommonTagEvent]);

  const showIcon = useCallback((name) => {
    setIconName(name);
    if (iconTimeout.current) clearTimeout(iconTimeout.current);
    iconTimeout.current = setTimeout(() => {
      setIconName('');
      iconTimeout.current = undefined;
    }, SHOW_PLAY_PAUSE_DELAY_TIME);
  }, []);

  const handlePlay = useCallback(() => {
    showIcon('play');
    handleUserInteraction();
    play();
    if (displayingNotMainControlsRef.current) {
      if (pauseOnShowControls) {
        if (miniEpgRefs.current.displaying) handleHideMiniEpg();
        if (tracksRefs.current.displaying) handleHideTracks();
        if (tapppPanelRefs.current.displaying) handleHideTapppPanel();
      }
      if (isStillWatchingActiveRef.current) handleContinueWatching();
    } else {
      showControls();
    }
    playerCommonTagEvent({ eventName: GTM_EVENT_NAMES.PLAYER.PLAY });
  }, [
    displayingNotMainControlsRef,
    handleContinueWatching,
    handleHideMiniEpg,
    handleHideTapppPanel,
    handleHideTracks,
    handleUserInteraction,
    isStillWatchingActiveRef,
    miniEpgRefs,
    tapppPanelRefs,
    pauseOnShowControls,
    play,
    playerCommonTagEvent,
    showControls,
    showIcon,
    tracksRefs,
  ]);

  const handlePause = useCallback(() => {
    showIcon('pause');
    handleUserInteraction();
    pause();
    if (!displayingNotMainControls) showControls();
    playerCommonTagEvent({ eventName: GTM_EVENT_NAMES.PLAYER.PAUSE });
  }, [
    displayingNotMainControls,
    handleUserInteraction,
    pause,
    playerCommonTagEvent,
    showControls,
    showIcon,
  ]);

  const handleTogglePlayPause = useCallback(() => {
    if (isPlayingRef.current) handlePause();
    else handlePlay();
  }, [isPlayingRef, handlePause, handlePlay]);

  const handleClickOverlay = useClickCallback(useCallback(() => {
    if (isScrubInProgressRef.current) return;
    if (tracksRefs.current.displaying) handleHideTracks();
    if (tapppPanelRefs.current.displaying) handleHideTapppPanel();
    else if (miniEpgRefs.current.displaying) handleHideMiniEpg();
    else {
      handleTogglePlayPause();
      showControlsAndReport();
    }
    handleUserInteraction();
  }, [
    isScrubInProgressRef,
    miniEpgRefs,
    tracksRefs,
    tapppPanelRefs,
    handleHideMiniEpg,
    handleHideTracks,
    handleTogglePlayPause,
    handleUserInteraction,
    handleHideTapppPanel,
    showControlsAndReport,
  ]));

  const handleBackToStart = useCallback(() => {
    seekingToStart.current = true;
    setProgress(0);
    setFocus(COMPONENTS.PROGRESS_BAR);
    hideControls();
    handleUserInteraction();
    gaService.sendEvent({
      action: REPORT_GA.GA_EVENT_ACTION_BACK_TO_START_FROM_PLAYER_BUTTON_CLICK,
      label: REPORT_GA.GA_EVENT_LABEL_BACK_TO_START_FROM_PLAYER_BUTTON_CLICK,
      category: REPORT_GA.GA_EVENT_CATEGORY_VOD_PLAYER,
    });
    playerCommonTagEvent({ eventName: GTM_EVENT_NAMES.PLAYER.WATCH_FROM_BEGINNING });
  }, [handleUserInteraction, hideControls, playerCommonTagEvent, setFocus, setProgress]);

  const handleSeekingStart = useCallback(() => {
    setIsScrubInProgress(true);
  }, []);

  const handleSeekingEnd = useCallback(() => {
    setIsScrubInProgress(false);
  }, []);

  const [scrubbing, setScrubbing] = useState(undefined);
  const setNextScrubbing = useCallback((newScrubbingDirection) => {
    setScrubbing((current) => {
      if (!current) {
        return { speed: ScrubbingSpeed.x1, direction: newScrubbingDirection };
      }

      const { speed, direction } = current;
      return {
        speed: direction === newScrubbingDirection ? nextSpeed(speed) : ScrubbingSpeed.x1,
        direction: newScrubbingDirection,
      };
    });
  }, []);

  const clearScrubbing = useCallback(() => {
    setScrubbing(undefined);
  }, []);

  const setMaxSpeedScrubbing = useCallback((direction) => {
    setScrubbing({ speed: SCRUBBING_MAX_SPEED, direction });
  }, []);

  const handleRewind = useCallback(() => {
    setIsScrubInProgress(true);
    showControls();
    setNextScrubbing(ScrubbingDirection.BACKWARD);
    sendTagEvent();
  }, [setNextScrubbing, showControls, sendTagEvent]);

  const handleFastForward = useCallback(() => {
    setIsScrubInProgress(true);
    showControls();
    setNextScrubbing(ScrubbingDirection.FORWARD);
    sendTagEvent(true);
  }, [setNextScrubbing, showControls, sendTagEvent]);

  const handleRewindLongPress = useCallback(() => {
    setIsScrubInProgress(true);
    showControls();
    setMaxSpeedScrubbing(ScrubbingDirection.BACKWARD);
    sendTagEvent();
  }, [
    setMaxSpeedScrubbing,
    showControls,
    sendTagEvent,
  ]);

  const handleFastForwardLongPress = useCallback(() => {
    setIsScrubInProgress(true);
    showControls();
    setMaxSpeedScrubbing(ScrubbingDirection.FORWARD);
    sendTagEvent(true);
  }, [setMaxSpeedScrubbing, showControls, sendTagEvent]);

  const handleSeekableEndReached = useCallback(() => {
    if (!isLive) setProgress(videoDuration - 5);
    clearScrubbing();
  }, [clearScrubbing, setProgress, videoDuration, isLive]);

  useOnKeyDown({
    listener: handleEnterPressSchedules,
    forKeyCodes: CHANNEL_BUTTONS,
    when: !displayingMiniEpg && isLive && !isScrubInProgress,
  });

  // Informs Parent if miniEpg is/isn't opened to prevent playback starts
  // when is webos V3 and miniEpg is opened
  useEffect(() => {
    onToggleMiniEpg(displayingMiniEpg);
  }, [displayingMiniEpg, onToggleMiniEpg]);

  // Sets the channelSwitchedInMiniEpg to false when content is playing again
  // to prevent wrong behavior with the showRipple
  useEffect(() => {
    if (isPlaying && !displayingMiniEpg) {
      setChannelSwitchedInMiniEpg(displayingMiniEpg);
    }
  }, [displayingMiniEpg, isPlaying]);

  // When back to start is completed start playback
  useEffect(() => {
    if (isSeeked && seekingToStart.current) {
      seekingToStart.current = false;
      play();
    }
  }, [isSeeked, play]);

  // When the content changed and tracks and mini epg are not displayed
  // set the focus to the progress bar
  useEffect(() => {
    if (isContentUpdated
      && !displayingTracks
      && !displayingMiniEpg
      && !displayingTapppPanel
      && !isStillWatchingActive) {
      lastFocused.current = COMPONENTS.PROGRESS_BAR;
      setFocus(COMPONENTS.PROGRESS_BAR);
    }
  }, [
    isContentUpdated,
    setFocus,
    displayingMiniEpg,
    displayingTapppPanel,
    displayingTracks,
    isStillWatchingActive,
  ]);

  // When the content changed and the progress bar is focused,
  // when the user go to the action buttons the focus should be
  // in the first button.
  useEffect(() => {
    if (isContentUpdated) {
      wasFocusedActionButtons.current = false;
    }
  }, [isContentUpdated]);

  // When there is a transition between one schedule and another
  // the tappp panel must be closed.
  const closeTapppPanelIfIsDisplaying = useCallback(() => {
    if (tapppPanelRefs.current.displaying) handleHideTapppPanel();
  }, [tapppPanelRefs, handleHideTapppPanel]);

  useEffect(() => {
    closeTapppPanelIfIsDisplaying();
  }, [contentId, closeTapppPanelIfIsDisplaying]);

  // Pause spatial navigation when no controls are displayed and
  // resume when they are.
  useEffect(() => {
    let timeout;
    if (displayingControls
      || displayingMiniEpg
      || displayingTracks
      || displayingTapppPanel
      || isStillWatchingActive) {
      timeout = setTimeout(() => {
        resumeSpatialNavigation();
      }, ACTIVATION_DURATION);
    } else {
      pauseSpatialNavigation();
    }

    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [
    resumeSpatialNavigation,
    pauseSpatialNavigation,
    displayingControls,
    displayingMiniEpg,
    displayingTapppPanel,
    displayingTracks,
    isStillWatchingActive,
  ]);

  // On unmount, resume spatial navigation if paused
  useEffect(() => {
    const currentResumeSpatialNavigation = resumeSpatialNavigation;
    return () => {
      currentResumeSpatialNavigation();
    };
  }, [resumeSpatialNavigation]);

  const title = useTitle(content, schedule);
  const subtitle = usePlayerSubtitle(content, schedule);

  const metadata = useMemo(
    () => getPlayerSecondSubtitle({
      schedule, content, timeZone, channelName,
    }),
    [content, schedule, timeZone, channelName],
  );

  const nextEpisodeLoaderTagEvent = useCallback(() => {
    playerCommonTagEvent({ eventName: GTM_EVENT_NAMES.PLAYER.NEXT_EPISODE_LOAD });
  }, [playerCommonTagEvent]);

  useEffect(() => {
    if (isNextEpisodeLoader.current && !isReadyToPlay) {
      nextEpisodeLoaderTagEvent();
      isNextEpisodeLoader.current = false;
    }
  }, [isReadyToPlay, nextEpisodeLoaderTagEvent]);

  const handleOnNextEpisode = useCallback(() => {
    playerCommonTagEvent({ eventName: GTM_EVENT_NAMES.PLAYER.NEXT_EPISODE });
    isNextEpisodeLoader.current = true;
    goToNextEpisode();
    hideControls();
  }, [goToNextEpisode, hideControls, playerCommonTagEvent]);

  const exitPlayerGtmEvent = useCallback(() => {
    if (displayingTracks) {
      playerCommonTagEvent({
        subcategory: GTM_SUBCATEGORIES.PLAYER.AUDIO_AND_SUBTITLES,
        eventName: GTM_EVENT_NAMES.PLAYER.CLOSE,
      });
    } else {
      playerCommonTagEvent({
        eventName: GTM_EVENT_NAMES.PLAYER.CLOSE,
      });
    }
    return true;
  }, [displayingTracks, playerCommonTagEvent]);

  useOnKeyDown({
    listener: exitPlayerGtmEvent,
    forKeyCodes: platform.keyCodes.mediaControl.stop,
  });

  // When the content is ready to play, show rating if content has it or
  // display controls if not.
  useEffect(() => {
    let timeout;
    if (isReadyToPlay && contentRatingRef.current) {
      showRating();
      timeout = setTimeout(hideRating, HIDE_RATING_AFTER_MS);
    }

    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [
    contentRatingRef,
    hideRating,
    isReadyToPlay,
    pauseOnShowControls,
    setFocus,
    showControls,
    showRating,
  ]);

  // When the controls are mounted restore the focus
  useEffect(() => {
    if (mountedControls) {
      setFocus(lastFocused.current);
    }
  }, [mountedControls, setFocus]);

  // When metadata content changed (different schedule on same station)
  // we need to update state to tell subcomponents
  useEffect(() => {
    if (contentId && previousContentId.current && previousContentId.current !== contentId) {
      previousContentId.current = contentId;
      setIsContentUpdated(true);
      if (!isLive) hideControls();
    }
  }, [contentId, hideControls, isLive]);

  // Restore isContentUpdated to false when was setted to true
  useEffect(() => {
    if (isContentUpdated) {
      setIsContentUpdated(false);
    }
  }, [isContentUpdated]);

  useOnLongPress({
    listener: handleRewind,
    forKeyCodes: platform.keyCodes.mediaControl.rewind,
    simulatedKeyCode: platform.keyCodes.mediaControl.rewindLong,
    simulatedKeyName: 'rewindLongPress',
    when: !displayingNotMainControls,
  });
  useOnLongPress({
    listener: handleFastForward,
    forKeyCodes: platform.keyCodes.mediaControl.fastForward,
    simulatedKeyCode: platform.keyCodes.mediaControl.fastForwardLong,
    simulatedKeyName: 'forwardLongPress',
    when: !displayingNotMainControls && (!isLive || isDelayed),
  });
  useOnKeyDown({
    listener: handleRewindLongPress,
    forKeyCodes: platform.keyCodes.mediaControl.rewindLong,
    when: !displayingNotMainControls,
  });
  useOnKeyDown({
    listener: handleFastForwardLongPress,
    forKeyCodes: platform.keyCodes.mediaControl.fastForwardLong,
    when: !displayingNotMainControls && (!isLive || isDelayed),
  });
  useOnBackPress({
    listener: returnFalse,
    when: isScrubInProgress,
    priority: 10,
  });
  useOnKeyDown({
    listener: handlePlay,
    forKeyCodes: platform.keyCodes.mediaControl.play,
    when: !isScrubInProgress,
  });
  useOnKeyDown({
    listener: handlePause,
    forKeyCodes: platform.keyCodes.mediaControl.pause,
    when: !isScrubInProgress,
  });
  useOnKeyDown({
    listener: handleTogglePlayPause,
    forKeyCodes: platform.keyCodes.mediaControl.playPause,
    when: !isScrubInProgress,
  });

  /* Interactivity Button Tappp Animation */
  useEffect(() => {
    if (shouldShowBetsInteractivityButton && isBetAllowed && displayingControls) {
      sessionStorage.setItem(PROFILE_VIEWED_INTERACTIVITY_BUTTON, true);
    }
  }, [shouldShowBetsInteractivityButton, isBetAllowed, displayingControls]);

  useEffect(() => {
    async function verifyTermsAgreement() {
      setTermsAgreementVerified(false);
      try {
        setCurrentPath(window.location.href);
        await performShouldUserAcceptNewTerms(iso2Code?.toLowerCase());
      } catch (error) {
        console.error('PlayerUI>verifyTermsAgreement', error);
      }
      setTermsAgreementVerified(true);
    }

    if (shouldShowBetsInteractivityButton
        && isTapppSDKActiveForOsVersion(platform.osName, sdkVersion)) {
      verifyTermsAgreement();
    }
  }, [
    iso2Code,
    sdkVersion,
    performShouldUserAcceptNewTerms,
    setCurrentPath,
    shouldShowBetsInteractivityButton,
  ]);

  if (isStillWatchingActive) {
    return (
      <AreYouStillWatching
        isLive={isLive}
        content={content}
        schedule={schedule}
        resumePlayback={handleContinueWatching}
        isStillWatchingActive={isStillWatchingActive}
        isFromInterstitial={isFromInterstitial}
      />
    );
  }

  return (
    <div
      className={PlayerUIClassNames.block({
        'right-menu-visible': displayingMiniEpg || displayingTracks || displayingTapppPanel,
      }, className)}
    >
      <Toast show={showToast} text={<FormattedMessage id={warningToastText} />} type="error" />
      <div className={PlayerUIClassNames.element('ui-container', (mountedTapppPanel && 'dtv-html5-c-player-ui__ui-container-tappp'))}>
        <InteractiveElement
          className={PlayerUIClassNames.element('overlay', {
            visible: displayingControls
            || displayingMiniEpg
            || displayingTracks,
          })}
          onMouseDown={handleClickOverlay}
        >
          <Icon
            name={iconName}
            className={PlayerUIClassNames.element('icon-player')}
          />
          <LoaderRipple
            className={PlayerUIClassNames.element('ripple')}
            shown={!iconName && showRipple}
          />
        </InteractiveElement>
        <LoaderScreen
          showMetadata={!channelSwitchedInMiniEpg}
          hidden={(!parentalControlBlock(content) && isReadyToPlay)}
          content={content}
          schedule={schedule}
          isContentRequestCompleted={isContentRequestCompleted}
          channelName={channelName}
        />
        {mountedRating && (
          <RatingInformation
            className={PlayerUIClassNames.element('rating-information', { visible: displayingRating })}
            content={content}
          />
        )}
        {mountedControls && (
          <div className={PlayerUIClassNames.element('player-controls', { visible: displayingControls })}>
            {(playerUpsellMessage && playerUpsellLink) && (
              <UpsellPlayerBanner
                message={playerUpsellMessage}
                link={playerUpsellLink}
              />
            )}
            <ConditionalVisible className={PlayerUIClassNames.element('player-controls-top')} visible={!isScrubInProgress}>
              <ContentInformation className={PlayerUIClassNames.element('content-information')}>
                <ContentInformation.Title>{title || <FormattedMessage id="common.noInfo" />}</ContentInformation.Title>
                <ContentInformation.Subtitle darken={!isLive}>
                  {subtitle}
                </ContentInformation.Subtitle>
                {!!metadata && (
                  <ContentInformation.Subtitle darken={isLive}>
                    {metadata}
                  </ContentInformation.Subtitle>
                )}
              </ContentInformation>
              <FocusableDiv
                focusKey={COMPONENTS.PLAYER_ACTION_BUTTONS}
                className={PlayerUIClassNames.element('action-buttons')}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onBecameFocused={handleFocusedActionButtons}
              >
                {!isLive && (
                  <FocusableIconButton
                    focusKey={COMPONENTS.BACK_TO_START}
                    className={PlayerUIClassNames.element('back-to-start-button')}
                    icon="refresh"
                    tooltipId="player.backToStart"
                    onEnterPress={handleBackToStart}
                    onBecameFocused={saveLastFocusedComponent}
                  />
                )}
                {isLive && (
                <>
                  <GoLive
                    isDelayed={isDelayed}
                    onEnterPress={handleEnterPressGoLive}
                    onBecameFocused={saveLastFocusedComponent}
                  />
                  <FocusableIconButton
                    className={PlayerUIClassNames.element('mini-epg-button')}
                    icon="mini-epg"
                    tooltipId="player.moreChannels"
                    focusKey={COMPONENTS.MINI_EPG_BUTTON}
                    onEnterPress={handleEnterPressSchedules}
                    onBecameFocused={saveLastFocusedComponent}
                  />
                </>
                )}
                {hasNextEpisode && !isLive && (
                  <FocusableIconButton
                    focusKey={COMPONENTS.NEXT_EPISODE}
                    className={PlayerUIClassNames.element('next-episode-button')}
                    icon="next-episode"
                    tooltipId="common.next"
                    onEnterPress={handleOnNextEpisode}
                    onBecameFocused={saveLastFocusedComponent}
                  />
                )}
                <FocusableIconButton
                  className={PlayerUIClassNames.element('tracks-button')}
                  icon="subtitles"
                  tooltipId="player.audioAndSubtitle"
                  focusKey={COMPONENTS.TRACK_BUTTON}
                  onEnterPress={handleEnterPressSubtitles}
                  onBecameFocused={saveLastFocusedComponent}
                />

                {shouldShowBetsInteractivityButton && (
                  <div className={showButtonAnimation ? 'dtv-html5-c-player-ui__tappp-icon-animation' : ''}>
                    <FocusableIconButton
                      className={PlayerUIClassNames.element('tapp-panel-button', (shouldDisableInteractivityButton && 'dtv-html5-c-focusable-icon-button--disabled'))}
                      icon="interactivity"
                      tooltipId={shouldDisableInteractivityButton ? '' : 'player.tapppPanel'}
                      focusKey={COMPONENTS.TAPPP_BUTTON}
                      onEnterPress={handleEnterPressTapppPanel}
                      onBecameFocused={saveLastFocusedComponent}
                      disabled={shouldDisableInteractivityButton}
                    />
                  </div>
                )}
              </FocusableDiv>
            </ConditionalVisible>
            <ProgressBarContainer
              focusKey={COMPONENTS.PROGRESS_BAR}
              className={PlayerUIClassNames.element('progress-bar-container')}
              isScrubInProgress={isScrubInProgress}
              isLive={isLive}
              isDelayed={isDelayed}
              scrubbing={scrubbing}
              togglePlayPause={handleTogglePlayPause}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              onSeekingStart={handleSeekingStart}
              onSeekingEnd={handleSeekingEnd}
              onSeekableStartReached={clearScrubbing}
              onSeekableEndReached={handleSeekableEndReached}
              onArrowPress={handleProgressBarArrowPress}
              onNextScrubbing={setNextScrubbing}
              onClearScrubbing={clearScrubbing}
              onBecameFocused={saveLastFocusedComponent}
            />
          </div>
        )}
      </div>
      <div className={PlayerUIClassNames.element('right-menu-container', (mountedTapppPanel && 'dtv-html5-c-player-ui__right-menu-tappp'))}>
        {mountedTracks && (
          <TracksControls
            className={PlayerUIClassNames.element('tracks-controls')}
            show={isScrubInProgress ? false : displayingTracks}
            audioTracks={audioTracks}
            subtitleTracks={subtitleTracks}
            selectAudioTrack={selectAudioTrack}
            selectSubtitleTrack={selectSubtitleTrack}
            disableSubtitles={disableSubtitles}
            hide={handleHideTracks}
          />
        )}
        {isLive && mountedMiniEpg && (
          <MiniEpg
            className={PlayerUIClassNames.element('mini-epg')}
            show={displayingMiniEpg}
            hide={handleHideMiniEpg}
            currentChannelId={channelId}
            channels={channels}
            selectedFilter={selectedFilter}
            handleFilterSelect={handleFilterSelect}
            isFetching={isFetching}
            filtersMiniEpg={filtersMiniEpg}
            homeOnBack={homeOnBack}
            focusable={displayingMiniEpg}
            currentChannelName={channelName}
            currentContent={content}
          />
        )}
        {(termsAgreementVerified && hasPlaybackAuthorizerSuccess) && (
          <TapppPanel
            className={PlayerUIClassNames.element('tappp-panel')}
            show={displayingTapppPanel}
            hide={handleHideTapppPanel}
            playerCommonTagEvent={playerCommonTagEvent}
            focusable={displayingTapppPanel}
            currentChannelId={channelId}
            channels={channels}
            content={content}
            schedule={schedule}
            isLive={isLive}
            handleEnterPressTapppPanel={handleEnterPressTapppPanel}
          />
        )}
      </div>
    </div>
  );
}

PlayerUI.propTypes = {
  className: PropTypes.string,
  pauseOnShowControls: PropTypes.bool,

  // Metadata
  schedule: PropTypes.shape({}),
  content: PropTypes.shape({}),
  isContentRequestCompleted: PropTypes.bool,
  channelId: PropTypes.string,
  isLive: PropTypes.bool,
  isBetAllowed: PropTypes.bool,
  showButtonAnimation: PropTypes.bool,
  hasNextEpisode: PropTypes.bool,
  isFromInterstitial: PropTypes.bool,
  homeOnBack: PropTypes.bool,
  counterEpisode: PropTypes.number,
  channelName: PropTypes.string,
  // Player State
  isReadyToPlay: PropTypes.bool,
  isPlaying: PropTypes.bool,
  isSeeked: PropTypes.bool,
  isBuffering: PropTypes.bool,
  isSourceUnloaded: PropTypes.bool,
  audioTracks: PropTypes.arrayOf(PropTypes.shape({})),
  subtitleTracks: PropTypes.arrayOf(PropTypes.shape({})),
  updateAudio: PropTypes.func,
  updateSubtitles: PropTypes.func,

  // Event listeners
  onUserInteraction: PropTypes.func,
  onToggleMiniEpg: PropTypes.func,

  // Business actions
  goToNextEpisode: PropTypes.func,

  // Player actions
  play: PropTypes.func,
  pause: PropTypes.func,
  setProgress: PropTypes.func.isRequired,
  selectAudioTrack: PropTypes.func.isRequired,
  selectSubtitleTrack: PropTypes.func.isRequired,
  disableSubtitles: PropTypes.func.isRequired,
  goToLive: PropTypes.func.isRequired,

  // Upper-state mutators
  setPlayerReducedSize: PropTypes.func,

  // Spatial navigation
  setFocus: PropTypes.func.isRequired,

  // Google Tags
  playerCommonTagEvent: PropTypes.func,

  // Upsell
  playerUpsellMessage: PropTypes.string,
  playerUpsellLink: PropTypes.string,

  hasPlaybackAuthorizerSuccess: PropTypes.bool,
};

export default memo(withFocusable()(memo(PlayerUI)));
